import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from "recharts";
import axios from "axios";

import api from "../auth/api";


// ===============================
// CONFIG
// ===============================
const CHART_H = 320;
const CHART_H_TALL = 360;
const LS_KEY = "wp_dashboard_range_v1";
const AUTOREFRESH_MS = 60_000; // 1 min

// ===============================
// HELPERS
// ===============================
/*async function getJSON(path, signal) {
  const url = path.startsWith("http") ? path : `${API}${path}`;
  const r = await fetch(url, { signal });
  const text = await r.text();
  if (!r.ok) throw new Error(`HTTP ${r.status} en ${url}:\n${text}`);
  try { return JSON.parse(text); }
  catch { throw new Error(`Respuesta no-JSON desde ${url}:\n${text.slice(0, 300)}`); }
}*/

async function getJSON(path, signal) {
  // Usamos la baseURL definida en src/auth/api.js
  // Soporta cancelación con AbortController (Axios >= v1)
  const { data } = await api.get(path, { signal });
  return data;
}

function isCanceledRequest(e) {
  return (
    e?.name === "AbortError" ||
    e?.name === "CanceledError" ||
    e?.code === "ERR_CANCELED" ||
    e?.message === "canceled" ||
    axios.isCancel?.(e)
  );
}

const fmtISO = (d) => d.toISOString().slice(0, 19).replace("T", " ");
const toLocalInput = (s) => s.replace(" ", "T");
const fromLocalInput = (s) => s.replace("T", " ");
const nf1 = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 1 });
const num = (v) => nf1.format(v ?? 0);
const pct = (v) => `${(((v ?? 0) * 100) || 0).toFixed(1)}%`;

function setURLRange({ from, to }) {
  const url = new URL(window.location.href);
  url.searchParams.set("from", from);
  url.searchParams.set("to", to);
  window.history.replaceState({}, "", url);
}
function getURLRange() {
  const url = new URL(window.location.href);
  const from = url.searchParams.get("from");
  const to = url.searchParams.get("to");
  return (from && to) ? { from, to } : null;
}

// ===============================
// MINIMAL CSS
// ===============================
const CSS = `
.wpdash .wrap > * + * {
  margin-top: 20px; /* separa bloques principales */
}

.wpdash .cards {
  margin-top: 20px;
}

.wpdash .grid {
  margin-top: 20px;
}

  .wpdash { color-scheme: dark; background: var(--bg); min-height: 100vh; }
  .wpdash * { box-sizing: border-box; }

  /* ---- Tema oscuro (default) ---- */
  .wpdash {
    --ink:#e5e7eb;          /* texto principal */
    --muted:#94a3b8;        /* texto secundario */
    --line:#1f2937;         /* líneas suaves */
    --bg:#0b1220;           /* fondo app */
    --paper:#0f1629;        /* tarjetas/paneles */
    --paper-2:#0c1324;
    --brand:#60a5fa;
    --ok:#22c55e; --warn:#f59e0b; --err:#ef4444; --info:#38bdf8;
  }

  .wpdash .wrap { max-width: 1220px; margin: 0 auto; padding: 16px 16px 64px; }
  .wpdash .hstack { display:flex; align-items:center; gap:12px }
  .wpdash .space { flex:1 }
  .wpdash .title { font-size: 22px; font-weight: 800; color: var(--ink); margin: 0; }
  .wpdash .subtitle { font-size: 12px; color: var(--muted); margin-top: 2px }

  /* Paneles simétricos */
  .wpdash .panel {
    background:var(--paper); border:1px solid var(--line); border-radius:14px;
    box-shadow: 0 2px 8px rgba(0,0,0,.35); overflow:hidden;
    display:flex; flex-direction:column; min-height: 100%;
  }
  .wpdash .panel + .panel { margin-top: 16px }
  .wpdash .panel-h {
    display:flex; align-items:center; justify-content:space-between; gap:10px;
    padding:10px 14px; border-bottom:1px solid var(--line); background:var(--paper-2);
  }
  .wpdash .panel-h h2 { font-size:14px; font-weight:700; color:var(--ink); margin:0; display:flex; align-items:center; gap:8px }
  .wpdash .panel-b { padding:14px; flex:1; display:flex; flex-direction:column; }
.wpdash .panel-b {
  padding: 20px; /* en vez de 14px */
}
  /* Filtros */
  .wpdash .filters { display:grid; grid-template-columns: repeat(4, minmax(220px,1fr)); gap:12px; align-items:end }
  @media (max-width: 980px){ .wpdash .filters { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 560px){ .wpdash .filters { grid-template-columns: 1fr; } }
  .wpdash .field { display:grid; gap:6px }
  .wpdash .label { font-size:12px; color: var(--muted) }
  .wpdash .input {
    height:38px; border:1px solid var(--line); border-radius:10px; padding:0 10px;
    font-size:13px; color:var(--ink); background:#0b1220;
  }
  .wpdash .input:focus { outline:2px solid rgba(96,165,250,.45); border-color:rgba(96,165,250,.6) }

  .wpdash .preset, .wpdash .btn {
    height:32px; padding:0 10px; border:1px solid var(--line); background:#0b1220; color:var(--ink);
    border-radius:10px; font-size:12px; cursor:pointer; transition:.15s background-color ease;
  }
  .wpdash .btn { height:36px; font-size:13px; }
  .wpdash .preset:hover, .wpdash .btn:hover { background:#101a2f }
  .wpdash .preset.is-active { border-color: var(--brand); color: var(--brand); background: rgba(96,165,250,.12) }
  .wpdash .btn.danger { border-color: rgba(239,68,68,.35); background: rgba(239,68,68,.12); color:#fecaca }

  .wpdash .switch { display:inline-flex; align-items:center; gap:8px; font-size:12px; color:var(--muted); user-select:none }
  .wpdash .switch input { width:40px; height:20px; appearance:none; background:#1f2937; border-radius:999px; position:relative; outline:none; cursor:pointer; transition:.2s }
  .wpdash .switch input:checked { background:#1e293b }
  .wpdash .switch input::after { content:""; position:absolute; top:2px; left:2px; width:16px; height:16px; border-radius:50%; background:#ffffff; transition:.2s }
  .wpdash .switch input:checked::after { transform: translateX(20px); }

  /* KPIs */
  .wpdash .cards { display:grid; grid-template-columns: repeat(6, minmax(0,1fr)); gap:12px }
  @media (max-width: 1180px) { .wpdash .cards { grid-template-columns: repeat(4, 1fr); } }
  @media (max-width: 840px)  { .wpdash .cards { grid-template-columns: repeat(3, 1fr); } }
  @media (max-width: 560px)  { .wpdash .cards { grid-template-columns: repeat(2, 1fr); } }
  .wpdash .kpi { border:1px solid var(--line); background:var(--paper); border-radius:12px; padding:14px;
                 min-height:100px; display:grid; grid-auto-rows:min-content min-content 1fr; gap:6px }
  .wpdash .kpi .k-title { font-size:11px; color:var(--muted) }
  .wpdash .kpi .k-value { font-size:20px; font-weight:700; color:var(--ink) }
  .wpdash .kpi .k-sub { font-size:11px; color:var(--muted) }

  /* Grillas de paneles: alturas iguales */
  .wpdash .grid { display:grid; grid-template-columns: 1fr 1fr; gap:16px; grid-auto-rows: 1fr; }
  @media (max-width: 980px) { .wpdash .grid { grid-template-columns: 1fr; } }

  /* Placeholders */
  .wpdash .skeleton { background:#111827; height:160px; border-radius:10px; animation:pulse 1.2s ease-in-out infinite; }
  .wpdash @keyframes pulse { 0%{opacity:.6} 50%{opacity:1} 100%{opacity:.6} }
  .wpdash .empty { text-align:center; padding:40px 0; color:var(--muted); font-size:13px }

  .wpdash .error { border:1px solid rgba(239,68,68,.35); background:rgba(239,68,68,.12); color:#fecaca; border-radius:12px; padding:10px 12px; }
  .wpdash .badge { font-size:11px; color:#cbd5e1; background:#0b1220; border:1px solid var(--line); padding:2px 8px; border-radius:999px }
`;

// Paleta por nombre de estado (normalizado)
const STATUS_COLORS = {
  'nuevo': '#60a5fa',
  'contactado': '#38bdf8',
  'en proceso': '#a78bfa',
  'seguimiento': '#f59e0b',
  'pendiente': '#94a3b8',
  'ganado': '#22c55e',
  'cerrado': '#22c55e',
  'perdido': '#ef4444',
  'rechazado': '#ef4444',
  'no interesado': '#ef4444',
};

// Fallback si el nombre no está en el mapa
const FALLBACK_PALETTE = [
  '#60a5fa', '#22c55e', '#f59e0b', '#a78bfa', '#38bdf8',
  '#ef4444', '#eab308', '#10b981', '#f472b6', '#94a3b8'
];

const norm = s => (s || '').toString().trim().toLowerCase();

const getSliceColor = (name, i) =>
  STATUS_COLORS[norm(name)] || FALLBACK_PALETTE[i % FALLBACK_PALETTE.length];


// ===============================
// ATOMS
// ===============================
const Icon = ({ name, size = 16, className = "" }) => {
  const icons = {
    chart: <path d="M3 3v18h18M7 17V9M12 17V5M17 17v-7" />,
    calendar: <path d="M8 2v4m8-4v4M3 10h18M5 6h14a2 2 0 0 1 2 2v12H3V8a2 2 0 0 1 2-2z" />,
    refresh: <path d="M3 12a9 9 0 1 0 3-6.7M3 3v6h6" />,
    clock: <path d="M12 6v6l4 2" />,
    bolt: <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />,
  };
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className={className}>{icons[name] || null}</svg>
  );
};

const Panel = ({ title, right, children }) => (
  <div className="panel">
    <div className="panel-h">
      <h2><Icon name="chart" size={16} /> {title}</h2>
      {right}
    </div>
    <div className="panel-b">{children}</div>
  </div>
);

const KPI = ({ title, value, sub, icon }) => (
  <div className="kpi" role="status" aria-live="polite">
    <div className="k-title">{icon}{title}</div>
    <div className="k-value">{value}</div>
    {sub ? <div className="k-sub">{sub}</div> : null}
  </div>
);

const Skeleton = ({ height = 180 }) => <div className="skeleton" style={{ height }} />;
const Empty = ({ children = "Sin datos en este rango." }) => <div className="empty">{children}</div>;

// ===============================
// MAIN
// ===============================
export default function Dashboard() {
  const initialRange = (() => {
    const fromURL = getURLRange();
    if (fromURL) return fromURL;
    const saved = localStorage.getItem(LS_KEY);
    if (saved) {
      try { const j = JSON.parse(saved); if (j?.from && j?.to) return j; } catch { }
    }
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - 30);
    return { from: fmtISO(from), to: fmtISO(to) };
  })();

  const [range, setRange] = useState(initialRange);
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const abortRef = useRef(null);

  const presets = useMemo(() => ([
    { label: "24h", shiftHours: 24 },
    { label: "7d", shift: 7 },
    { label: "30d", shift: 30 },
    { label: "90d", shift: 90 },
    { label: "Hoy", today: true },
  ]), []);

  // Active preset calc
  const activePreset = useMemo(() => {
    const from = new Date(range.from);
    const to = new Date(range.to);
    const deltaMs = to - from;
    const oneDay = 24 * 3600 * 1000;
    for (const p of presets) {
      if (p.today) {
        const isToday =
          from.toDateString() === new Date(to.getFullYear(), to.getMonth(), to.getDate(), 0, 0, 0).toDateString();
        if (isToday) return p.label;
      } else if (p.shiftHours) {
        if (Math.round(deltaMs / 3600000) === p.shiftHours) return p.label;
      } else if (p.shift) {
        if (Math.round(deltaMs / oneDay) === p.shift) return p.label;
      }
    }
    return null;
  }, [range, presets]);

  // Data loader
  async function loadData(signal) {
    setLoading(true); setError("");
    const q = `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;
    try {
      const [ov, ts, fu, tp, ag] = await Promise.all([
        getJSON(`/api/reports/overview${q}`, signal),
        getJSON(`/api/reports/timeseries${q}`, signal),
        getJSON(`/api/reports/leads-funnel${q}`, signal),
        getJSON(`/api/reports/templates${q}`, signal),
        getJSON(`/api/reports/agents${q}`, signal),
      ]);
      setOverview(ov || null);
      setSeries(ts?.series || null);
      setFunnel(fu?.funnel || []);
      setTemplates(tp?.templates || []);
      setAgents(ag?.agents || []);
      setLastUpdated(new Date());
    } catch (e) {
      if (isCanceledRequest(e)) return;
      setError(e.message || "Error desconocido");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }

  // Effect: fetch on range change
  useEffect(() => {
    // persist & sync URL
    localStorage.setItem(LS_KEY, JSON.stringify(range));
    setURLRange(range);

    // abort previous
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    loadData(controller.signal);
    return () => controller.abort();
  }, [range.from, range.to]);

  // Effect: auto refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      abortRef.current?.abort();
      const c = new AbortController();
      abortRef.current = c;
      loadData(c.signal);
    }, AUTOREFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, range.from, range.to]);

  // Preset setter
  const setPreset = (p) => {
    const to = new Date(); let from = new Date();
    if (p.today) from = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 0, 0, 0);
    else if (p.shiftHours) from = new Date(to.getTime() - p.shiftHours * 3600 * 1000);
    else from.setDate(to.getDate() - p.shift);
    const next = { from: fmtISO(from), to: fmtISO(to) };
    setRange(next);
  };

  // Series merge
  const merged = useMemo(() => {
    if (!series) return [];
    const keys = ["sent", "delivered", "read", "failed", "inbound"];
    const map = new Map();
    for (const k of keys) {
      for (const p of (series[k] || [])) {
        const row = map.get(p.d) || { d: p.d };
        row[k] = p.v;
        map.set(p.d, row);
      }
    }
    return Array.from(map.values()).sort((a, b) => a.d.localeCompare(b.d));
  }, [series]);

  // KPIs derivados
  const k = overview?.kpis || {};
  const totalsByStatus = useMemo(() => {
    const s = series || {};
    const sum = (arr) => (arr || []).reduce((a, b) => a + (b?.v || 0), 0);
    return {
      sent: sum(s.sent),
      delivered: sum(s.delivered),
      read: sum(s.read),
      failed: sum(s.failed),
      inbound: sum(s.inbound),
    };
  }, [series]);

  const leadsTotal = useMemo(() => (funnel || []).reduce((a, b) => a + (b?.total || 0), 0), [funnel]);
  const isWon = (status) => /won|ganad|cerrad|sale|success/i.test(status || "");
  const leadsWon = useMemo(
    () => (funnel || []).filter(f => isWon(f.status)).reduce((a, b) => a + (b?.total || 0), 0),
    [funnel]
  );
  const conversion = leadsTotal ? leadsWon / leadsTotal : 0;
  const agentsActive = (agents || []).filter(a => (a?.messages_sent || 0) > 0).length || 0;
  const templatesUsed = (templates || []).filter(t => (t?.sent || 0) > 0).length || 0;

  const donutMsgs = useMemo(() => {
    const sent = (k.sent ?? totalsByStatus.sent) || 0;
    const delivered = (k.delivered ?? totalsByStatus.delivered) || 0;
    const read = (k.read ?? totalsByStatus.read) || 0;
    const failed = (k.failed ?? totalsByStatus.failed) || 0;

    const leidos = Math.max(0, read);
    const entregadosNoLeidos = Math.max(0, delivered - read);
    const pendientes = Math.max(0, sent - delivered);
    const fallidos = Math.max(0, failed);

    return [
      { name: "Leídos", value: leidos, key: "read" },
      { name: "Entregados (no leídos)", value: entregadosNoLeidos, key: "delivered_only" },
      { name: "Pendientes", value: pendientes, key: "pending" },
      { name: "Fallidos", value: fallidos, key: "failed" },
    ];
  }, [k, totalsByStatus]);

  const donutColors = { read: "#22c55e", delivered_only: "#0ea5e9", pending: "#a3a3a3", failed: "#ef4444" };
  const leadsPie = useMemo(() => (funnel || []).map(f => ({ name: f.status, value: f.total })), [funnel]);
  const barsEstado = useMemo(() => ([
    { status: "Enviados", total: totalsByStatus.sent },
    { status: "Entregados", total: totalsByStatus.delivered },
    { status: "Leídos", total: totalsByStatus.read },
    { status: "Fallidos", total: totalsByStatus.failed },
    { status: "Entrantes", total: totalsByStatus.inbound },
  ].filter(x => x.total > 0)), [totalsByStatus]);

  const topN = (arr, key, n = 5) => [...(arr || [])].sort((a, b) => (b?.[key] || 0) - (a?.[key] || 0)).slice(0, n);
  const topTemplates = useMemo(() => topN(templates, "sent", 5).map(t => ({ name: t.template, sent: t.sent })), [templates]);
  const topAgents = useMemo(() => topN(agents, "messages_sent", 5).map(a => ({ name: a.agent, messages_sent: a.messages_sent })), [agents]);

  // ===============================
  // UI
  // ===============================
  return (
    <>
      <style>{CSS}</style>

      <div className="wpdash">
        <div className="wrap">
          {/* Encabezado */}
          <div className="hstack" style={{ marginBottom: 12 }}>
            <Icon name="chart" size={18} />
            <div>
              <h1 className="title">Dashboard de Reportes</h1>
              <div className="subtitle">WhatsPlatinum • métricas de envíos y atención</div>
            </div>
            <span className="space" />
            <span className="badge" title="Zona horaria del navegador">
              <Icon name="clock" size={14} /> TZ: {Intl.DateTimeFormat().resolvedOptions().timeZone || "local"}
            </span>
          </div>

          {/* Panel Filtros */}
          <Panel
            title="Filtros"
            right={
              <div className="hstack" role="group" aria-label="Acciones">
                <label className="switch" title="Refrescar automáticamente cada 1 min">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    aria-label="Auto actualizar"
                  />
                  Auto-refresh
                </label>
                <button
                  className="btn"
                  onClick={() => {
                    abortRef.current?.abort();
                    const c = new AbortController();
                    abortRef.current = c;
                    loadData(c.signal);
                  }}
                  aria-label="Refrescar ahora"
                  title="Refrescar ahora"
                >
                  <Icon name="refresh" /> Refrescar
                </button>
              </div>
            }
          >
            <div className="filters">
              <div className="field">
                <label className="label" htmlFor="from">Desde</label>
                <div className="hstack">
                  <Icon name="calendar" size={14} />
                  <input
                    id="from"
                    type="datetime-local"
                    className="input"
                    value={toLocalInput(range.from)}
                    onChange={e => setRange(r => ({ ...r, from: fromLocalInput(e.target.value) }))}
                  />
                </div>
              </div>

              <div className="field">
                <label className="label" htmlFor="to">Hasta</label>
                <div className="hstack">
                  <Icon name="calendar" size={14} />
                  <input
                    id="to"
                    type="datetime-local"
                    className="input"
                    value={toLocalInput(range.to)}
                    onChange={e => setRange(r => ({ ...r, to: fromLocalInput(e.target.value) }))}
                  />
                </div>
              </div>

              <div className="field">
                <label className="label">Presets</label>
                <div className="hstack" style={{ flexWrap: "wrap", gap: 8 }}>
                  {presets.map(p => (
                    <button
                      key={p.label}
                      className={`preset ${activePreset === p.label ? "is-active" : ""}`}
                      onClick={() => setPreset(p)}
                      aria-pressed={activePreset === p.label}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="field">
                <label className="label">Estado</label>
                <div className="hstack" style={{ gap: 8 }}>
                  <span className="badge" title="Última actualización">
                    <Icon name="bolt" size={14} /> {lastUpdated ? lastUpdated.toLocaleString() : (loading ? "Cargando..." : "—")}
                  </span>
                  {loading && <span className="badge">Cargando…</span>}
                </div>
              </div>
            </div>

            {error && (
              <div className="error" style={{ marginTop: 12 }}>
                {error}
                <div className="hstack" style={{ marginTop: 8 }}>
                  <button className="btn danger" onClick={() => setRange({ ...range })}>Reintentar</button>
                </div>
              </div>
            )}
          </Panel>

          {/* KPIs */}
          <div className="cards" style={{ marginTop: 16 }}>
            {!overview ? (
              Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} height={96} />)
            ) : (
              <>
                <KPI title="Enviados" value={num(k.sent ?? totalsByStatus.sent)} sub={`Total en rango`} />
                <KPI title="Entregados" value={num(k.delivered ?? totalsByStatus.delivered)} sub={`Tasa ${pct(k.deliveryRate)}`} />
                <KPI title="Leídos" value={num(k.read ?? totalsByStatus.read)} sub={`Tasa lectura ${pct(k.readRate)}`} />
                <KPI title="Fallidos" value={num(k.failed ?? totalsByStatus.failed)} sub={`Tasa ${pct(k.failRate)}`} />
                <KPI title="Entrantes" value={num(totalsByStatus.inbound)} sub="Conversaciones entrantes" />
                <KPI title="1ª respuesta" value={`${(k.avgFirstReplyMin ?? 0).toFixed(1)} min`} sub="Tiempo promedio" />
                <KPI title="Leads totales" value={num(leadsTotal)} sub="Embudo" />
                <KPI title="Leads ganados" value={num(leadsWon)} sub="Cerrados/ganados" />
                <KPI title="Conversión" value={pct(conversion)} sub="Ganados / Leads" />
                <KPI title="Agentes activos" value={num(agentsActive)} sub="Con mensajes" />
                <KPI title="Plantillas usadas" value={num(templatesUsed)} sub="Con envíos" />
                <KPI title="Ventana" value={`${new Date(range.from).toLocaleDateString()} → ${new Date(range.to).toLocaleDateString()}`} />
              </>
            )}
          </div>

          {/* Charts */}
          <div className="grid" style={{ marginTop: 16 }}>
            <Panel title="Serie diaria">
              {!series ? (
                <Skeleton height={CHART_H_TALL} />
              ) : merged.length ? (
                <div style={{ width: "100%", height: CHART_H_TALL }}>
                  <ResponsiveContainer>
                    <LineChart data={merged}>
                      <CartesianGrid stroke="rgba(148,163,184,.18)" />
                      <XAxis dataKey="d" stroke="#94a3b8" />
                      <YAxis allowDecimals={false} stroke="#94a3b8" />
                      <Tooltip
                        wrapperStyle={{ outline: 'none' }}              // evita borde del focus
                        contentStyle={{
                          background: '#0f1629',                        // fondo oscuro
                          border: '1px solid #1f2937',
                          borderRadius: 8,
                          boxShadow: '0 8px 28px rgba(0,0,0,.35)',
                          color: '#e5e7eb',                             // color base de texto
                        }}
                        labelStyle={{ color: '#e5e7eb', fontWeight: 600 }}  // título (fecha/etiqueta)
                        itemStyle={{ color: '#cbd5e1' }}                    // filas (serie: valor)
                      />
                      <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                      <Line type="monotone" dataKey="sent" name="Enviados" />
                      <Line type="monotone" dataKey="delivered" name="Entregados" />
                      <Line type="monotone" dataKey="read" name="Leídos" />
                      <Line type="monotone" dataKey="failed" name="Fallidos" />
                      <Line type="monotone" dataKey="inbound" name="Entrantes" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <Empty />}
            </Panel>

            <Panel title="Estado de envío (dona)">
              {!overview ? (
                <Skeleton height={CHART_H} />
              ) : donutMsgs.reduce((a, b) => a + b.value, 0) === 0 ? (
                <Empty>No hay datos.</Empty>
              ) : (
                <div style={{ width: "100%", height: CHART_H }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Tooltip
                        wrapperStyle={{ outline: 'none' }}              // evita borde del focus
                        contentStyle={{
                          background: '#0f1629',                        // fondo oscuro
                          border: '1px solid #1f2937',
                          borderRadius: 8,
                          boxShadow: '0 8px 28px rgba(0,0,0,.35)',
                          color: '#e5e7eb',                             // color base de texto
                        }}
                        labelStyle={{ color: '#e5e7eb', fontWeight: 600 }}  // título (fecha/etiqueta)
                        itemStyle={{ color: '#cbd5e1' }}                    // filas (serie: valor)
                      />
                      <Legend />
                      <Pie data={donutMsgs} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="80%" paddingAngle={2}>
                        {donutMsgs.map((e, i) => <Cell fill={{ read: '#22c55e', delivered_only: '#38bdf8', pending: '#6b7280', failed: '#ef4444' }[e.key]} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Panel>
          </div>

          <div className="grid">
            <Panel title="Volumen por estado (rango)">
  {!barsEstado.length ? (
    <Empty />
  ) : (
    <div style={{ width: "100%", height: CHART_H }}>
      <ResponsiveContainer>
        <BarChart data={barsEstado}>
          {/* Grilla y ejes en modo oscuro */}
          <CartesianGrid stroke="rgba(148,163,184,.18)" />
          <XAxis
            dataKey="status"
            stroke="#94a3b8"                 // color de la línea del eje
            tick={{ fill: '#cbd5e1', fontSize: 12 }}  // color de los labels
          />
          <YAxis
            allowDecimals={false}
            stroke="#94a3b8"
            tick={{ fill: '#cbd5e1', fontSize: 12 }}
          />

          {/* Tooltip oscuro legible */}
          <Tooltip
            wrapperStyle={{ outline: 'none' }}
            contentStyle={{
              background: '#0f1629',
              border: '1px solid #1f2937',
              borderRadius: 8,
              boxShadow: '0 8px 28px rgba(0,0,0,.35)',
              color: '#e5e7eb',
            }}
            labelStyle={{ color: '#e5e7eb', fontWeight: 600 }}
            itemStyle={{ color: '#cbd5e1' }}
          />

          {/* Barra con color visible */}
          <Bar dataKey="total" name="Total" fill="#60a5fa" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )}
</Panel>


            <Panel title="Leads por estado (torta)">
              {!leadsPie.length ? (
                <Empty>Sin leads en este rango.</Empty>
              ) : (
                <div style={{ width: "100%", height: CHART_H }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Tooltip
                        wrapperStyle={{ outline: 'none' }}              // evita borde del focus
                        contentStyle={{
                          background: '#0f1629',                        // fondo oscuro
                          border: '1px solid #1f2937',
                          borderRadius: 8,
                          boxShadow: '0 8px 28px rgba(0,0,0,.35)',
                          color: '#e5e7eb',                             // color base de texto
                        }}
                        labelStyle={{ color: '#e5e7eb', fontWeight: 600 }}  // título (fecha/etiqueta)
                        itemStyle={{ color: '#cbd5e1' }}                    // filas (serie: valor)
                      />
                      <Legend
  wrapperStyle={{ color: '#cbd5e1' }}
  formatter={(value) => <span style={{ color:'#cbd5e1' }}>{value}</span>}
/>
                      <Pie
                        data={leadsPie}
                        dataKey="value"
                        nameKey="name"
                        outerRadius="80%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                      >
                        {leadsPie.map((slice, i) => (
                          <Cell key={i} fill={getSliceColor(slice.name, i)} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Panel>
          </div>

        </div>
      </div>
    </>
  );
}
